// Desktop UI route table — the wide, sidebar-driven layout.
//
// This is the existing React experience, unchanged. It renders inside the router
// and theme provider owned by src/App.jsx, and reads all data through src/core.

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useApp } from '../../core/state/AppContext';
import { kioskLandingRoute } from '../../core/wall/kioskMode';

import AppLayout from './components/AppLayout';
import LoginScreen from './pages/LoginScreen';
import OtpScreen from './pages/OtpScreen';
import DesktopShell from './pages/DesktopShell';
import RoomsScreen from './pages/RoomsScreen';
import DeviceScreen from './pages/device/DeviceScreen';
import SchedulesScreen from './pages/SchedulesScreen';
import FavouritesScreen from './pages/FavouritesScreen';
import ScenesScreen from './pages/ScenesScreen';
import RoomScenesScreen from './pages/scenes/RoomScenesScreen';
import ProfileScreen from './pages/ProfileScreen';
import CompaniesScreen from './pages/CompaniesScreen';
import DeviceTypesScreen from './pages/DeviceTypesScreen';
import UserAccessScreen from './pages/UserAccessScreen';
import InstalledDevicesScreen from './pages/InstalledDevicesScreen';
import DeviceAdminScreen from './pages/DeviceAdminScreen';
import MqttConsoleScreen from './pages/MqttConsoleScreen';
import TuyaTestingScreen from './pages/TuyaTestingScreen';
import FirmwareScreen from './pages/FirmwareScreen';
import WallModeScreen from './pages/wall/WallModeScreen';
import WallPanelSetupScreen from '../shared/WallPanelSetupScreen';
import KioskSetupScreen from './pages/KioskSetupScreen';
import AboutScreen from './pages/AboutScreen';

// ⚠️ NO BLE ROUTE HERE, deliberately. `/ble-test` was added to this shell and
// then removed: a desktop browser cannot drive Bluetooth usefully (see the note
// at the top of core/ble/bleClient), so the page could only ever report why it
// was unavailable. The BLE bench lives in the mobile shell — MobileRoutes.jsx —
// where the radio actually exists.

function Protected({ children }) {
  const { isAuthed, signingIn } = useApp();
  // Hold the route while a saved sign-in is in flight. Redirecting on the first
  // paint would show the login form to someone who is already being signed in,
  // and the form would then vanish under them a moment later.
  if (signingIn) return <SigningIn />;
  return isAuthed ? children : <Navigate to="/login" replace />;
}

function SigningIn() {
  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 2,
      bgcolor: 'background.default',
    }}
    >
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">Signing you in…</Typography>
    </Box>
  );
}

export default function DesktopRoutes() {
  const { isAuthed, isSuperAdmin, canTuyaTest, signingIn } = useApp();

  return (
    <Routes>
      {/* The login form must not appear while a saved sign-in is running, and
          must not appear at all to someone already signed in — landing on
          /login by bookmark or Back should take them home, not ask again. */}
      <Route
        path="/login"
        element={
          // eslint-disable-next-line no-nested-ternary
          signingIn ? <SigningIn />
            : isAuthed ? <Navigate to="/home" replace />
              : <LoginScreen />
        }
      />
      <Route path="/otp" element={<OtpScreen />} />

      {/* Kiosk full-screen — deliberately without the app header. */}
      {/* ⚠️ THERE IS ONE WALL EDITOR, and it is /wall/setup. A second designer
          (/wall/design, free-flow panels with mqtt+delay sequences) used to live
          alongside it, so "settings" meant two different screens depending on
          which button you had pressed. Removed rather than hidden — a route
          nobody links to is a screen nobody maintains. */}
      <Route path="/wall" element={<Protected><WallModeScreen /></Protected>} />

      {/* Everything else renders under the persistent header. */}
      <Route element={<Protected><AppLayout /></Protected>}>
        <Route path="/home" element={<DesktopShell />} />
        <Route path="/rooms/:locationId" element={<RoomsScreen />} />
        <Route path="/room/:roomId/scenes" element={<RoomScenesScreen />} />
        <Route path="/device/:uniqueNumber" element={<DeviceScreen />} />
        <Route path="/schedule/:uniqueNumber/:output" element={<SchedulesScreen />} />
        <Route path="/favourites" element={<FavouritesScreen />} />
        <Route path="/scenes" element={<ScenesScreen />} />
        <Route path="/profile" element={<ProfileScreen />} />
        <Route path="/about" element={<AboutScreen />} />
        {/* Design the current location's fixed wall sheet. */}
        <Route path="/wall/setup" element={<WallPanelSetupScreen />} />
        <Route path="/wall/kiosk" element={<KioskSetupScreen />} />
        <Route
          path="/companies"
          element={isSuperAdmin ? <CompaniesScreen /> : <Navigate to="/home" replace />}
        />
        <Route
          path="/device-types"
          element={isSuperAdmin ? <DeviceTypesScreen /> : <Navigate to="/home" replace />}
        />
        {/* Admin: find a device by Unique Number and assign it. Gated here as
            well as inside the page — it writes ownership. */}
        <Route
          path="/device-admin"
          element={isSuperAdmin ? <DeviceAdminScreen /> : <Navigate to="/home" replace />}
        />
        {/* Admin: one user, everything they can reach. Writes other people's
            access, so it is gated here as well as inside the page. */}
        <Route
          path="/user-access"
          element={isSuperAdmin ? <UserAccessScreen /> : <Navigate to="/home" replace />}
        />
        {/* Admin: what is installed on a customer's premises — the piece count,
            NOT the per-user access list. See the note in the screen. */}
        <Route
          path="/installed"
          element={isSuperAdmin ? <InstalledDevicesScreen /> : <Navigate to="/home" replace />}
        />
        {/* MQTT message console — Super Admin diagnostics. */}
        <Route
          path="/mqtt"
          element={isSuperAdmin ? <MqttConsoleScreen /> : <Navigate to="/home" replace />}
        />
        {/* Production bench: the most recently seen devices on the broker,
            regardless of owner. It writes DeviceType and reboots hardware, so it
            is gated here as well as inside the page — on the AllowTestingTuya
            permission, not IsSuperAdmin; see AppContext's canTuyaTest. */}
        <Route
          path="/tuya-testing"
          element={canTuyaTest ? <TuyaTestingScreen /> : <Navigate to="/home" replace />}
        />
        {/* Every device's firmware, estate-wide. Reflashes hardware, so it is
            gated here as well as inside the page. */}
        <Route
          path="/firmware"
          element={isSuperAdmin ? <FirmwareScreen /> : <Navigate to="/home" replace />}
        />
      </Route>

      {/* A wall tablet lands on its panel instead of the normal home. Only the
            LANDING route is redirected — forcing /wall on every navigation would
            make the designer unreachable from the very tablet it configures. */}
        <Route
          path="*"
          element={
            // eslint-disable-next-line no-nested-ternary
            !isAuthed ? <Navigate to="/login" replace />
              : <Navigate to={kioskLandingRoute() || '/home'} replace />
          }
        />
    </Routes>
  );
}
